<template>
  <div class="home-container">
    <!-- ===== 头部导航 ===== -->
    <header class="header" :class="{ 'header-scrolled': isScrolled }">
      <div class="container header-inner">
        <div class="logo-area" @click="scrollToTop">
          <span class="logo-icon">⚙️</span>
          <span class="logo-text">HUAWAY · 华魏WMS</span>
        </div>
        <div class="nav-menu">
          <a
            v-for="item in navItems"
            :key="item.id"
            href="#"
            :class="{ active: activeSection === item.id }"
            @click.prevent="scrollToSection(item.id)"
          >{{ item.label }}</a>
        </div>
        <div class="header-btns">
          <el-button class="btn-outline" @click="goToLogin">登录</el-button>
          <el-button class="btn-primary" @click="applyTrial">免费试用</el-button>
        </div>
      </div>
    </header>

    <main>
      <!-- ===== Banner 区 ===== -->
      <section id="home" class="banner">
        <div class="container">
          <div class="banner-content">
            <div class="banner-badge">🚀 弘锋机器人 9月10日正式上线</div>
            <h1>华魏WMS —— 制造业仓储智能管理平台</h1>
            <p>序列号级追溯 · 三现收货防伪 · 维纳斯件版本化管理 · 与ERP/MES无缝协同</p>
            <div class="banner-buttons">
              <el-button class="btn-primary btn-large" @click="applyTrial">立即免费试用</el-button>
              <el-button class="btn-outline btn-large" @click="contactSales">预约演示</el-button>
            </div>
            <div class="banner-trust">
              <span>✅ 已服务 10+ 制造企业</span>
              <span>✅ 国家发明专利受理</span>
              <span>✅ 工位MES原生集成</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== 数据统计 ===== -->
      <section class="stats">
        <div class="container">
          <div class="stats-grid">
            <div class="stat-item">
              <span class="stat-number">10+</span>
              <span class="stat-label">制造企业客户</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">50,000+</span>
              <span class="stat-label">日均库存操作</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">99.9%</span>
              <span class="stat-label">库存准确率</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">100%</span>
              <span class="stat-label">序列号追溯覆盖</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== 核心优势 ===== -->
      <section id="features" class="advantages">
        <div class="container">
          <h2 class="section-title">为什么选择华魏WMS</h2>
          <div class="grid-4">
            <div class="advantage-card">
              <div class="advantage-icon">🔒</div>
              <h3>安全可靠</h3>
              <p>乐观锁防并发超卖，库存流水只增不删，负库存物理阻断，银行级风控。</p>
              <a href="#" class="learn-more" @click.prevent="showDetail('security')">了解更多 →</a>
            </div>
            <div class="advantage-card">
              <div class="advantage-icon">📱</div>
              <h3>降本增效</h3>
              <p>微信小程序替代工业PDA，员工自带手机，硬件成本直降90%。</p>
              <a href="#" class="learn-more" @click.prevent="showDetail('cost')">了解更多 →</a>
            </div>
            <div class="advantage-card">
              <div class="advantage-icon">⚡</div>
              <h3>简易部署</h3>
              <p>SaaS模式，云端开通即用，支持私有化，30分钟完成配置。</p>
              <a href="#" class="learn-more" @click.prevent="showDetail('deploy')">了解更多 →</a>
            </div>
            <div class="advantage-card">
              <div class="advantage-icon">🔄</div>
              <h3>工位协同</h3>
              <p>与工位MES原生打通，完工自动入库，领料自动扣减，业财一体。</p>
              <a href="#" class="learn-more" @click.prevent="showDetail('mes')">了解更多 →</a>
            </div>
          </div>
          <div class="section-cta">
            <el-button class="btn-primary" @click="applyTrial">立即体验华魏WMS →</el-button>
          </div>
        </div>
      </section>

      <!-- ===== 产品功能亮点 ===== -->
      <section id="solutions" class="features">
        <div class="container">
          <h2 class="section-title">核心功能覆盖全仓储场景</h2>
          <div class="features-grid">
            <div class="feature-item" v-for="item in features" :key="item.title">
              <div class="feature-icon">{{ item.icon }}</div>
              <h4>{{ item.title }}</h4>
              <p>{{ item.desc }}</p>
            </div>
          </div>
          <div class="section-cta">
            <el-button class="btn-outline-cta" @click="viewAllFeatures">查看全部功能 →</el-button>
          </div>
        </div>
      </section>

      <!-- ===== 最新动态 / 弘锋机器人里程碑 ===== -->
      <section id="cases" class="milestone">
        <div class="container">
          <h2 class="section-title">弘锋机器人 × 华魏WMS 上线里程碑</h2>
          <p class="section-sub">从现场验证到全面上线，华魏WMS为弘锋机器人提供全链路仓储数字化支撑</p>

          <div class="timeline">
            <div class="timeline-item">
              <div class="timeline-dot"></div>
              <div class="timeline-content">
                <div class="timeline-date">2026年6月</div>
                <div class="timeline-title">✅ 现场初步验证成功</div>
                <div class="timeline-desc">
                  华魏WMS在弘锋机器人生产现场完成全流程验证，涵盖物料管理、扫码出入库、库存追溯等核心场景，系统稳定性与业务匹配度获客户认可。
                </div>
              </div>
            </div>

            <div class="timeline-item">
              <div class="timeline-dot active"></div>
              <div class="timeline-content">
                <div class="timeline-date">2026年9月10日</div>
                <div class="timeline-title">🚀 弘锋机器人正式上线</div>
                <div class="timeline-desc">
                  华魏WMS在弘锋机器人全面上线，覆盖采购入库、生产领料、销售出库、库存管理全业务场景，实现仓储管理全面数字化。
                </div>
                <div class="timeline-badge">即将上线</div>
              </div>
            </div>

            <div class="timeline-item">
              <div class="timeline-dot future"></div>
              <div class="timeline-content">
                <div class="timeline-date">2026年12月</div>
                <div class="timeline-title">🌟 一体化解决方案发布</div>
                <div class="timeline-desc">
                  华魏将推出「工位MES + WMS」一体化解决方案，从工序级物流到仓储管理全链路打通，助力制造企业实现车间与仓库的实时协同。
                </div>
                <div class="timeline-badge future">敬请期待</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== 底部CTA ===== -->
      <section class="bottom-cta">
        <div class="container">
          <h2>准备开启仓储数字化之旅？</h2>
          <p>立即体验华魏WMS，30分钟快速开通</p>
          <div class="cta-buttons">
            <el-button class="btn-primary btn-large" @click="applyTrial">免费试用</el-button>
            <el-button class="btn-outline btn-large" @click="contactSales">咨询解决方案</el-button>
          </div>
        </div>
      </section>
    </main>

    <!-- ===== 页脚 ===== -->
    <footer class="footer">
      <div class="container">
        <div class="footer-inner">
          <div>
            <div style="font-size: 20px; font-weight: 600; margin-bottom: 12px; color: #fff;">HUAWAY · 华魏智能</div>
            <div style="font-size: 14px; color: #9bb7d4;">赋能制造业 · 可信赖的仓储数字底座</div>
          </div>
          <div class="footer-links">
            <div>
              <a href="#" @click.prevent="aboutUs">关于我们</a><br><br>
              <a href="#" @click.prevent="contactUs">联系方式</a>
            </div>
            <div>
              <a href="#" @click.prevent="privacyPolicy">隐私政策</a><br><br>
              <a href="#" @click.prevent="userAgreement">用户协议</a>
            </div>
            <div>
              <span>📞 13203212923</span><br><br>
              <span>✉️ quecanran@huawaytech.com</span>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <div class="footer-copyright">
            <span>© 2026 HUAWAY 湖南华魏智能装备有限公司</span>
            <span>湘ICP备2026014631号-2</span>
          </div>
        </div>
      </div>
    </footer>

    <!-- ===== 浮动CTA ===== -->
    <div class="floating-cta" @click="applyTrial">
      <span class="floating-icon">🚀</span>
      <span class="floating-text">免费试用</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      isScrolled: false,
      activeSection: 'home',
      navItems: [
        { id: 'home', label: '首页' },
        { id: 'features', label: '产品功能' },
        { id: 'solutions', label: '解决方案' },
        { id: 'cases', label: '客户案例' },
        { id: 'help', label: '帮助中心' }
      ],
      features: [
        { icon: '📍', title: '货位管理', desc: '多级货位，扫码快速定位，库存实时可视。' },
        { icon: '📸', title: '三现收货', desc: '强制拍照+GPS/WiFi+时间水印，杜绝伪造入库。' },
        { icon: '🔄', title: '维纳斯件管理', desc: '残缺件版本化管理，再制造全生命周期追溯。' },
        { icon: '📱', title: '扫码出入库', desc: '微信小程序扫码，替代工业PDA，硬件成本降90%。' },
        { icon: '📋', title: '库存流水追溯', desc: '每笔变动可追溯，操作人/时间/前后数量完整记录。' },
        { icon: '⚡', title: '工位MES协同', desc: '与工位MES原生打通，完工自动入库，领料自动扣减。' }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    this.observeSections()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50
    },
    observeSections() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.activeSection = entry.target.id
          }
        })
      }, { threshold: 0.3 })
      this.navItems.forEach(item => {
        const el = document.getElementById(item.id)
        if (el) observer.observe(el)
      })
    },
    scrollToSection(id) {
      const el = document.getElementById(id)
      if (el) {
        const offset = 70
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset
        window.scrollTo({ top, behavior: 'smooth' })
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    goToLogin() {
      this.$router.push('/login')
    },
    applyTrial() {
      this.$message.info('免费试用申请通道即将开放，敬请期待。')
    },
    contactSales() {
      this.$message.info('商务咨询：quecanran@huawaytech.com')
    },
    showDetail(type) {
      const map = {
        security: '华魏WMS采用乐观锁防并发、库存流水只增不删、负库存物理阻断，确保库存数据绝对准确。',
        cost: '员工使用微信小程序即可完成出入库操作，无需采购工业PDA，硬件成本直降90%。',
        deploy: 'SaaS模式云端开通即用，支持私有化部署，30分钟完成基础配置。',
        mes: '与工位MES原生打通，完工自动触发入库，领料自动扣减库存，业财一体。'
      }
      this.$message.info(map[type] || '更多详情请咨询销售。')
    },
    viewAllFeatures() {
      this.$message.info('全部功能列表即将发布，敬请期待。')
    },
    aboutUs() {
      this.$message.info('湖南华魏智能装备有限公司，专注制造业、农牧业的数字化、智能化。')
    },
    contactUs() {
      this.$message.info('邮箱：quecanran@huawaytech.com | 电话：13203212923')
    },
    privacyPolicy() {
      this.$message.info('隐私政策内容即将公布。')
    },
    userAgreement() {
      this.$message.info('用户协议内容即将公布。')
    }
  }
}
</script>

<style scoped>
/* ===== 基础重置 ===== */
.home-container {
  font-family: 'Segoe UI', 'PingFang SC', Roboto, 'Helvetica Neue', sans-serif;
  background-color: #fff;
  color: #1f2d3d;
  line-height: 1.5;
}
.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ===== 头部导航 ===== */
.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  box-shadow: 0 1px 12px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;
  transition: box-shadow 0.3s;
}
.header-scrolled {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}
.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  flex-wrap: wrap;
}
.logo-area {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.logo-icon {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #1E88E5, #0b5e9e);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.logo-text {
  font-size: 22px;
  font-weight: 600;
  color: #1E88E5;
}
.nav-menu {
  display: flex;
  gap: 32px;
  align-items: center;
}
.nav-menu a {
  text-decoration: none;
  color: #2c3e50;
  font-weight: 500;
  transition: color 0.2s;
  cursor: pointer;
  font-size: 15px;
  position: relative;
}
.nav-menu a::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 0;
  height: 2px;
  background: #1E88E5;
  transition: width 0.3s;
}
.nav-menu a:hover::after,
.nav-menu a.active::after {
  width: 100%;
}
.nav-menu a:hover {
  color: #1E88E5;
}
.nav-menu a.active {
  color: #1E88E5;
}
.header-btns {
  display: flex;
  gap: 12px;
}
.btn-outline {
  background: transparent;
  border: 1px solid #1E88E5;
  padding: 8px 20px;
  border-radius: 30px;
  color: #1E88E5;
  cursor: pointer;
  transition: 0.2s;
  font-weight: 500;
  font-size: 14px;
}
.btn-outline:hover {
  background: #e9f3fc;
}
.btn-primary {
  background: #1E88E5;
  border: none;
  padding: 8px 24px;
  border-radius: 30px;
  color: white;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: 0.2s;
}
.btn-primary:hover {
  background: #0b5e9e;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(30, 136, 229, 0.4);
}
.btn-large {
  padding: 14px 36px;
  font-size: 16px;
}
.btn-outline-cta {
  background: transparent;
  border: 1px solid #1E88E5;
  padding: 10px 28px;
  border-radius: 30px;
  color: #1E88E5;
  cursor: pointer;
  font-weight: 500;
  transition: 0.2s;
}
.btn-outline-cta:hover {
  background: #e9f3fc;
  transform: translateY(-1px);
}

/* ===== Banner ===== */
.banner {
  background: linear-gradient(105deg, #f0f7ff 0%, #e3f0fc 100%);
  padding: 80px 0 70px;
  min-height: 560px;
  display: flex;
  align-items: center;
}
.banner-content {
  text-align: center;
}
.banner-badge {
  display: inline-block;
  background: #1E88E5;
  color: white;
  padding: 4px 20px;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
}
.banner h1 {
  font-size: 42px;
  font-weight: 700;
  color: #0f2b3d;
  margin-bottom: 16px;
  line-height: 1.2;
}
.banner p {
  font-size: 18px;
  color: #3a546d;
  max-width: 700px;
  margin: 0 auto 32px;
}
.banner-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}
.banner-trust {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 32px;
  font-size: 14px;
  color: #4a6a8a;
}
.banner-trust span {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ===== 数据统计 ===== */
.stats {
  background: #0a2540;
  padding: 48px 0;
}
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  text-align: center;
}
.stat-item {
  display: flex;
  flex-direction: column;
}
.stat-number {
  font-size: 40px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 1px;
}
.stat-label {
  font-size: 15px;
  color: #9bb7d4;
  margin-top: 6px;
}

/* ===== 核心优势 ===== */
.advantages {
  padding: 70px 0;
  background: #fff;
}
.section-title {
  text-align: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1e4663;
}
.section-sub {
  text-align: center;
  color: #6a8aa8;
  font-size: 16px;
  margin-bottom: 48px;
}
.section-cta {
  text-align: center;
  margin-top: 40px;
}
.grid-4 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 32px;
}
.advantage-card {
  text-align: center;
  padding: 30px 20px;
  background: #f8fafc;
  border-radius: 24px;
  transition: transform 0.3s, box-shadow 0.3s;
}
.advantage-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.06);
}
.advantage-icon {
  font-size: 48px;
  margin-bottom: 16px;
}
.advantage-card h3 {
  font-size: 20px;
  margin-bottom: 10px;
  color: #1E88E5;
}
.advantage-card p {
  color: #4a627a;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 12px;
}
.learn-more {
  color: #1E88E5;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.learn-more:hover {
  text-decoration: underline;
}

/* ===== 产品功能 ===== */
.features {
  background: #f9fbfd;
  padding: 70px 0;
}
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}
.feature-item {
  background: white;
  border-radius: 20px;
  padding: 30px 24px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  border: 1px solid #eef2f6;
  transition: all 0.3s;
}
.feature-item:hover {
  border-color: #cde3f5;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);
  transform: translateY(-4px);
}
.feature-icon {
  font-size: 40px;
  margin-bottom: 16px;
}
.feature-item h4 {
  font-size: 18px;
  margin-bottom: 10px;
  color: #1e4663;
}
.feature-item p {
  color: #5d6f83;
  font-size: 14px;
  line-height: 1.6;
}

/* ===== 时间线 / 里程碑 ===== */
.milestone {
  padding: 70px 0;
  background: #fff;
}
.timeline {
  max-width: 820px;
  margin: 0 auto;
  position: relative;
  padding-left: 40px;
}
.timeline::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 12px;
  bottom: 20px;
  width: 3px;
  background: #d0e2f0;
}
.timeline-item {
  position: relative;
  margin-bottom: 40px;
}
.timeline-dot {
  position: absolute;
  left: -32px;
  top: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #b0c8db;
  border: 3px solid #fff;
  box-shadow: 0 0 0 3px #d0e2f0;
}
.timeline-dot.active {
  background: #1E88E5;
  box-shadow: 0 0 0 3px #1E88E5;
  animation: pulse-dot 2s infinite;
}
.timeline-dot.future {
  background: #f5a623;
  box-shadow: 0 0 0 3px #f5a623;
}
@keyframes pulse-dot {
  0%, 100% { box-shadow: 0 0 0 3px rgba(30, 136, 229, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(30, 136, 229, 0.1); }
}
.timeline-content {
  background: #f8fafc;
  border-radius: 16px;
  padding: 20px 24px;
  transition: background 0.3s;
}
.timeline-content:hover {
  background: #f0f4f9;
}
.timeline-date {
  font-size: 13px;
  color: #1E88E5;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.timeline-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e4663;
  margin-bottom: 6px;
}
.timeline-desc {
  font-size: 15px;
  color: #4a627a;
  line-height: 1.6;
}
.timeline-badge {
  display: inline-block;
  margin-top: 10px;
  padding: 2px 14px;
  background: #1E88E5;
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}
.timeline-badge.future {
  background: #f5a623;
}

/* ===== 底部CTA ===== */
.bottom-cta {
  background: linear-gradient(105deg, #0a2540 0%, #123a5a 100%);
  padding: 60px 0;
  text-align: center;
}
.bottom-cta h2 {
  font-size: 32px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 10px;
}
.bottom-cta p {
  font-size: 18px;
  color: #9bb7d4;
  margin-bottom: 28px;
}
.cta-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}
.cta-buttons .btn-outline {
  border-color: rgba(255, 255, 255, 0.4);
  color: #fff;
}
.cta-buttons .btn-outline:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* ===== 页脚 ===== */
.footer {
  background: #061e30;
  color: #9bb7d4;
  padding: 48px 0 20px;
}
.footer-inner {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 32px;
}
.footer-links {
  display: flex;
  gap: 48px;
  flex-wrap: wrap;
}
.footer-links a {
  color: #9bb7d4;
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
}
.footer-links a:hover {
  color: white;
}
.footer-links span {
  font-size: 14px;
}
.footer-bottom {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #1a3a5f;
  text-align: center;
}
.footer-copyright {
  display: flex;
  justify-content: center;
  gap: 24px;
  font-size: 13px;
  color: #6a8aa8;
  flex-wrap: wrap;
}

/* ===== 浮动CTA ===== */
.floating-cta {
  position: fixed;
  right: 24px;
  bottom: 32px;
  z-index: 999;
  background: linear-gradient(135deg, #1E88E5, #0b5e9e);
  color: white;
  padding: 12px 20px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 8px 28px rgba(30, 136, 229, 0.45);
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
  border: none;
  font-weight: 600;
  font-size: 15px;
}
.floating-cta:hover {
  transform: scale(1.05) translateY(-3px);
  box-shadow: 0 12px 36px rgba(30, 136, 229, 0.55);
}
.floating-icon {
  font-size: 20px;
}

/* ===== 响应式 ===== */
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
}
@media (max-width: 900px) {
  .header-inner {
    flex-direction: column;
    height: auto;
    padding: 12px 0;
    gap: 12px;
  }
  .nav-menu {
    gap: 20px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .banner h1 {
    font-size: 32px;
  }
  .grid-4 {
    gap: 20px;
  }
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .stat-number {
    font-size: 30px;
  }
}
@media (max-width: 640px) {
  .banner-buttons {
    flex-direction: column;
    align-items: center;
  }
  .banner-trust {
    flex-direction: column;
    gap: 8px;
  }
  .banner {
    padding: 50px 0 40px;
    min-height: auto;
  }
  .banner h1 {
    font-size: 26px;
  }
  .timeline {
    padding-left: 28px;
  }
  .timeline-dot {
    left: -22px;
    width: 14px;
    height: 14px;
  }
  .timeline-content {
    padding: 16px;
  }
  .timeline-title {
    font-size: 17px;
  }
  .features-grid {
    grid-template-columns: 1fr;
  }
  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }
  .floating-cta {
    padding: 10px 16px;
    font-size: 13px;
    right: 16px;
    bottom: 20px;
  }
  .floating-cta .floating-text {
    display: none;
  }
  .footer-copyright {
    flex-direction: column;
    gap: 8px;
  }
}
</style>